import axios from "axios";
import { getAuthHeader } from "./auth";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export async function fetchBillingPlans() {
  const response = await axios.get(`${API_BASE_URL}/api/billing/plans/`);
  return response.data;
}

export async function fetchBillingSubscription(sessionId) {
  const headers = await getAuthHeader();
  const params = sessionId ? { session_id: sessionId } : {};
  const response = await axios.get(`${API_BASE_URL}/api/billing/subscription/`, {
    headers,
    params,
  });
  return response.data;
}

export async function startBillingCheckout(plan, invitationToken = null) {
  const headers = await getAuthHeader();
  const body = { plan };
  if (invitationToken) {
    body.invitation_token = invitationToken;
  }
  const response = await axios.post(
    `${API_BASE_URL}/api/billing/checkout/`,
    body,
    { headers }
  );
  return response.data;
}

export async function openBillingPortal() {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${API_BASE_URL}/api/billing/portal/`,
    {},
    { headers }
  );
  return response.data;
}

// Both return the same shape as fetchBillingSubscription, so callers can swap
// the result straight into state without a follow-up read.
export async function cancelBillingSubscription() {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${API_BASE_URL}/api/billing/cancel/`,
    {},
    { headers }
  );
  return response.data;
}

export async function resumeBillingSubscription() {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${API_BASE_URL}/api/billing/resume/`,
    {},
    { headers }
  );
  return response.data;
}

export const PAID_PLAN_IDS = ["organizer_intro", "organizer_standard", "enterprise"];

export function isPaidPlan(plan) {
  return PAID_PLAN_IDS.includes(plan);
}

export const SUPPORT_EMAIL = "support@civixthread.com";

// Keyed by the `code` the billing API returns. Copy lives here so checkout on
// the pricing page, the join page, and account details all say the same thing.
const BILLING_ERROR_MESSAGES = {
  upgrade_disabled:
    "Upgrades are paused while we finish some maintenance. Please check back shortly.",
  billing_not_configured: `We couldn't start checkout right now. Please try again in a few minutes. If it keeps happening, email ${SUPPORT_EMAIL}.`,
  billing_misconfigured: `We couldn't start checkout right now. Please try again in a few minutes. If it keeps happening, email ${SUPPORT_EMAIL}.`,
  invalid_plan: "That plan isn't available. Refresh the page and choose a plan again.",
  not_eligible:
    "Paid plans are for organization, municipality, and official accounts. Change your account type in Account details to upgrade.",
  billing_forbidden:
    "Only the billing owner or an admin on this account can change the plan. Ask them to make this change for you.",
  already_subscribed:
    "You already have an active subscription. Use Manage billing to change or cancel it.",
  no_billing_account:
    "You don't have a paid plan yet, so there's nothing to manage. Upgrade first.",
  no_active_subscription:
    "You don't have a paid plan to change yet. Upgrade first.",
  card_declined: "Your card was declined. Try a different payment method.",
  stripe_error: `We couldn't start checkout right now. Please try again in a few minutes. If it keeps happening, email ${SUPPORT_EMAIL}.`,
  plan_unavailable: `We couldn't start checkout right now. Please try again in a few minutes. If it keeps happening, email ${SUPPORT_EMAIL}.`,
  email_invalid:
    "Update the email on your account in Account details, then try again.",
  rate_limited: "Checkout is busy right now. Wait a few seconds and try again.",
  attempt_in_progress:
    "Your last attempt is still processing. Give it a minute before trying again.",
  payment_provider_unreachable:
    "Check your internet connection and try again.",
};

// Codes whose message the API builds per-case (card decline reasons, support
// reference IDs), so the server's text is more specific than anything here.
const SERVER_WORDED_CODES = new Set([
  "card_declined",
  "billing_misconfigured",
  "plan_unavailable",
  "stripe_error",
]);

/**
 * Turn a billing API failure into a message that tells the user what happened
 * and what to do next. `action` completes a sentence, e.g. "start checkout".
 */
export function getBillingErrorMessage(error, { action = "start checkout" } = {}) {
  const data = error?.response?.data;
  const code = data?.code;
  const serverMessage =
    typeof data?.error === "string" && data.error.trim() ? data.error.trim() : null;

  // A coded response is one the API worded for the user, so it beats any
  // status-based guess we could make here.
  if (code && serverMessage && SERVER_WORDED_CODES.has(code)) {
    return serverMessage;
  }
  if (code && BILLING_ERROR_MESSAGES[code]) {
    return BILLING_ERROR_MESSAGES[code];
  }

  if (!error?.response) {
    return `We couldn't reach CivixThread to ${action}. Check your internet connection and try again.`;
  }
  if (code && serverMessage) {
    return serverMessage;
  }

  const status = error.response.status;

  if (status === 401) {
    return `Your session expired. Sign in again to ${action}.`;
  }
  if (status === 403) {
    return `Your account doesn't have permission to ${action}. Ask the billing owner on this account for access.`;
  }
  if (status === 429) {
    return BILLING_ERROR_MESSAGES.rate_limited;
  }
  if (serverMessage) {
    return serverMessage;
  }
  if (status >= 500) {
    return `We couldn't ${action} right now. Please try again in a few minutes. If it keeps happening, email ${SUPPORT_EMAIL}.`;
  }

  return `We couldn't ${action} just now. Try again. If it keeps happening, email ${SUPPORT_EMAIL}.`;
}

export function isPlanLimitError(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  return (
    status === 402 ||
    code === "plan_limit_reached" ||
    code === "plan_recurring_not_available"
  );
}

export const PLAN_LIMIT_USER_MESSAGE = "Upgrade your plan to continue.";
export const PLAN_LIMIT_UPGRADE_PATH = "/account-details#billing";

// The API's `error` / `upgrade_url` are ignored: older deployments return quota
// details and a legacy /pricing link, which redirects away from account billing.
export function getPlanLimitUserFacing(error) {
  if (!isPlanLimitError(error)) return null;
  return {
    message: PLAN_LIMIT_USER_MESSAGE,
    upgradeHref: PLAN_LIMIT_UPGRADE_PATH,
  };
}
